import { colors, ActionModal } from '@insurancemenu/imenu-components-lib';
import {
  Archive,
  CheckCircle,
  DoubleArrowRight,
  NavigationClose,
} from '@insurancemenu/imenu-components-lib/dist/icons';
import { ModalType } from 'global/utils/ModalsHelper';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { MissclikableAction, WarningModals } from 'store/modules/upload-documents/documentDelegate';
import { ActionUiPresenter, DocumentAction } from 'types/documentsService';
import {
  APPROVE_WARNING_MODAL_TEST_ID,
  BYPASS_WARNING_MODAL_TEST_ID,
  DISCARD_WARNING_MODAL_TEST_ID,
  REJECT_WARNING_MODAL_TEST_ID,
  REMOVE_UPLOAD_WARNING_MODAL_TEST_ID,
  SEND_SIGN_REMINDER_WARNING_MODAL_TEST_ID,
} from '../constants';

function renderModal(action: ActionUiPresenter, warningModals: WarningModals): React.ReactNode {
  if (!Object.values(MissclikableAction).includes(action.type as unknown as MissclikableAction)) {
    return null;
  }
  const warningModal = warningModals[action.type as unknown as MissclikableAction];
  switch (action.type) {
    case DocumentAction.APPROVE:
      return (
        <ActionModal
          key={action.type}
          title="Approve document"
          subtitle="Are you sure you want to approve this document?"
          show={warningModal.isVisible}
          onClose={warningModal.close}
          closeButtonText="No"
          actionButtonText="Yes"
          onAction={async () => {
            warningModal.close();
            await action.handler();
          }}
          icon={<CheckCircle color={colors.green[600]} width="5.6rem" />}
          data-test-id={APPROVE_WARNING_MODAL_TEST_ID}
        />
      );
    case DocumentAction.REJECT:
      return (
        <ActionModal
          key={action.type}
          title="Reject document"
          subtitle="Are you sure you want to reject this document?"
          show={warningModal.isVisible}
          onClose={warningModal.close}
          closeButtonText="No"
          actionButtonText="Yes"
          onAction={async () => {
            warningModal.close();
            await action.handler();
          }}
          icon={<NavigationClose color={colors.red[600]} width="5.6rem" />}
          data-test-id={REJECT_WARNING_MODAL_TEST_ID}
        />
      );
    case DocumentAction.REMOVE_UPLOAD:
      return (
        <ActionModal
          key={action.type}
          title="Remove uploaded document"
          subtitle="This action will delete the uploaded file. You still will be able to upload the new file via Upload Document button. Are you sure you want to proceed?"
          show={warningModal.isVisible}
          onClose={warningModal.close}
          closeButtonText="No"
          actionButtonText="Yes"
          onAction={async () => {
            warningModal.close();
            await action.handler();
          }}
          modalType={ModalType.DELETE}
          data-test-id={REMOVE_UPLOAD_WARNING_MODAL_TEST_ID}
        />
      );
    case DocumentAction.BYPASS:
      return (
        <ActionModal
          key={action.type}
          title="Bypass document"
          subtitle="This action will mark the document as bypassed, allowing to continue without uploading it. Are you sure you want to proceed?"
          show={warningModal.isVisible}
          onClose={warningModal.close}
          closeButtonText="No"
          actionButtonText="Yes"
          onAction={async () => {
            warningModal.close();
            await action.handler();
          }}
          icon={<DoubleArrowRight width="5.6rem" color={colors.orange[600]} />}
          data-test-id={BYPASS_WARNING_MODAL_TEST_ID}
        />
      );
    case DocumentAction.SEND_SIGN_REMINDER:
      return (
        <ActionModal
          key={action.type}
          title="Send sign reminder"
          subtitle="Remind the requested recipient to upload the document?"
          show={warningModal.isVisible}
          onClose={warningModal.close}
          closeButtonText="No"
          actionButtonText="Yes"
          onAction={async () => {
            warningModal.close();
            await action.handler();
          }}
          modalType={ModalType.NOTIFICATION}
          data-test-id={SEND_SIGN_REMINDER_WARNING_MODAL_TEST_ID}
        />
      );
    case DocumentAction.DISCARD:
      return (
        <ActionModal
          key={action.type}
          title="Discard document"
          subtitle="This will remove the document from the required list. Are you sure you want to proceed?"
          show={warningModal.isVisible}
          onClose={warningModal.close}
          closeButtonText="No"
          actionButtonText="Yes"
          onAction={async () => {
            warningModal.close();
            await action.handler();
          }}
          icon={<Archive color={colors.red[600]} width="5.6rem" />}
          data-test-id={DISCARD_WARNING_MODAL_TEST_ID}
        />
      );

    default:
      return null;
  }
}

interface Props {
  availableActions: ActionUiPresenter[];
  warningModals: WarningModals;
}

const WarningModals: React.FC<Props> = observer(({ availableActions, warningModals }) => {
  return <>{availableActions.map((actionObj) => renderModal(actionObj, warningModals))}</>;
});

export default WarningModals;
